import React, { useMemo, useState } from 'react';

import { ROLLUP_BRANCH_NUMBER, SITE_LIST_FIELDS, newPeopleValues, normalizeRow, validateRow } from './utils';
import { FormSelectField, FormTextField, ModalShell } from '../../components';

import './styles.scoped.css';

/**
 * CP-857: add / edit one SiteList row.
 *
 * Save stays enabled and validates on press (house rule) — errors render inline under the
 * field that produced them. Server-side rejections (duplicate key, branch not in the
 * operational list) arrive as one form-level message, because those are not attributable to
 * a single field the user can see.
 *
 * The natural key is (Branch Number, LOB), so editing either of those moves the row's
 * identity. That is intentional — it is how a wrong branch number gets fixed — and the API
 * duplicate-checks the destination before allowing it.
 */
/**
 * An abbreviated field (SL, DBD, RRM, LOB) shows its full name on hover, so the form does not
 * have to spell "Service Lead" out in a label that then no longer matches the grid header
 * or the CSV column it maps to.
 */
const fieldLabel = field =>
  field.hint ? (
    <span className="field-hint" title={field.hint}>
      {field.label}
    </span>
  ) : (
    field.label
  );

const SiteListForm = ({ row, isEdit, lobOptions, lobLoading, knownValues, onSave, onDelete, onCancel }) => {
  const [form, setForm] = useState(() => normalizeRow(row || {}));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const busy = saving || deleting;

  const isRollup = useMemo(
    () => String(form.BranchNumber).trim() === String(ROLLUP_BRANCH_NUMBER),
    [form.BranchNumber],
  );

  // Names nobody else uses yet. Not blocking -- a new recruiter has to be typed once -- but
  // it catches the typo/whitespace variants that quietly split rows in the BI joins.
  const newValues = useMemo(() => newPeopleValues(form, knownValues), [form, knownValues]);

  const handleChange = (key, value) => {
    setForm(current => ({ ...current, [key]: value }));
    // clear this field's error as soon as it is edited, and drop any stale server message
    setErrors(current => (current[key] ? { ...current, [key]: undefined } : current));
    setFormError('');
  };

  const handleSave = () => {
    const normalized = normalizeRow(form);
    const nextErrors = validateRow(normalized, { lobOptions });

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    setFormError('');
    // Clearing the in-flight flag belongs in catch, not finally: on success the parent
    // closes this dialog, so a finally would set state on an unmounted component.
    onSave(normalized).catch(e => {
      setFormError(e.message);
      setSaving(false);
    });
  };

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleting(true);
    setFormError('');
    onDelete().catch(e => {
      setFormError(e.message);
      setConfirmingDelete(false);
      setDeleting(false);
    });
  };

  // Save / (Delete) / Cancel. `disabled` here is the in-flight reentrancy guard only --
  // Save is never disabled to gate missing input, it validates on press.
  const buttons = [{ text: saving ? 'Saving…' : 'Save', onPress: handleSave, disabled: busy }];
  if (isEdit) {
    buttons.push({
      text: confirmingDelete ? 'Confirm Delete' : 'Delete',
      variant: 'warning',
      onPress: handleDelete,
      disabled: busy,
    });
  }
  buttons.push({ text: 'Cancel', variant: 'outline', onPress: onCancel, disabled: busy });

  return (
    <ModalShell
      title={isEdit ? 'Edit SiteList Row' : 'Add SiteList Row'}
      subtitle={isEdit ? `Branch ${row.BranchNumber} / ${row.LOB}` : 'Branch Number and LOB together identify the row.'}
      buttons={buttons}>
      <div className="form-grid">
        {SITE_LIST_FIELDS.map(field =>
          field.type === 'select' ? (
            <FormSelectField
              key={field.key}
              label={fieldLabel(field)}
              required={field.required}
              options={lobOptions}
              loading={lobLoading}
              disabled={busy}
              error={!!errors[field.key]}
              errorMessage={errors[field.key]}
              value={lobOptions.find(option => option.value === form[field.key]) || null}
              onChange={option => handleChange(field.key, option?.value ?? '')}
            />
          ) : (
            <FormTextField
              key={field.key}
              label={fieldLabel(field)}
              type={field.type}
              required={field.required}
              disabled={busy}
              error={!!errors[field.key]}
              errorMessage={errors[field.key]}
              value={form[field.key]}
              onChange={event => handleChange(field.key, event.target.value)}
            />
          ),
        )}
      </div>

      {isRollup && (
        <p className="form-note">
          Branch Number {ROLLUP_BRANCH_NUMBER} is the reserved rollup row — it is not checked against the operational
          branch list.
        </p>
      )}

      {!!newValues.length && (
        <p className="form-note">
          New value for {newValues.join(', ')} — not used by any other row. Check the spelling if this should match an
          existing name.
        </p>
      )}

      {confirmingDelete && !formError && (
        <p className="form-warning">
          Deleting removes this row from BI on the next dataset refresh. The previous values are kept in the change
          history. Press Confirm Delete to continue.
        </p>
      )}

      {!!formError && <p className="form-error">{formError}</p>}
    </ModalShell>
  );
};

export default SiteListForm;
